import type { TFunction } from 'i18next';
import { Observer, observer } from 'mobx-react-lite';
import posthog from 'posthog-js';
import type { JSX } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router';

import {
  type CustomField,
  type CustomFields as CustomFieldsCollection,
  type Importer,
} from '@feathr/blackbox';
import {
  ActionBar,
  Button,
  parseStepFromHash,
  Spinner,
  Step,
  Steps,
  toast,
  useHashSync,
  Wizard,
  WizardV2,
} from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import { useAccount, useFlags, useLocalUrl, useStore, useUser } from '@feathr/extender/state';
import { isImportsRevampEnabled } from '@feathr/extender/utils/importsRevampFlags';
import { logUserEvents, useRedirect } from '@feathr/hooks';

import { isContactsOnlyImport, isImporterEditable } from './DataImportPage.utils';
import DataImportStepOne, { StepOneNextButton, validateStepOne } from './DataImportStepOne';
import DataImportStepThree from './DataImportStepThree';
import DataImportStepTwo, { StepTwoNextButton, validateStepTwo } from './DataImportStepTwo';

import styles from './DataImportPage.css';

export interface IImportButtonProps {
  importer: Importer;
  disabled?: boolean;
}

const LAST_STEP = 2;

const importerIsValid = (errors: string[][]): boolean => {
  return errors.every((stepErrors) => stepErrors.length === 0);
};

const validateImporter = (importer: Importer, t: TFunction): string[][] => {
  return [validateStepOne(importer), validateStepTwo(importer, t)];
};

export const ImportButton = observer(({ importer, disabled }: IImportButtonProps) => {
  const account = useAccount();
  const { CustomFields } = useStore();
  const localUrl = useLocalUrl();
  const [redirect] = useRedirect();
  const { t } = useTranslation();

  const url = localUrl('/data/imports');
  const validationErrors = validateImporter(importer, t);
  const isValid = importerIsValid(validationErrors);

  async function handleClick(): Promise<void> {
    await onSave(importer, CustomFields, url, redirect, t, account.id);
  }

  const buttonText = importer.isOptOut ? t('Import opt out list') : t('Import data');

  return (
    <Button disabled={disabled || !isValid} onClick={handleClick} type={'primary'}>
      {buttonText}
    </Button>
  );
});

async function saveModel(model: CustomField): Promise<void> {
  if (model.isEphemeral || !model.id) {
    await model.collection!.add(model, { validate: true, refreshApiCache: false });
  } else if (model.isDirty) {
    await model.save();
  }
}

async function handleSave(importer: Importer, CustomFields: CustomFieldsCollection): Promise<void> {
  /*
   * Save custom fields before importer so they are available by the time
   * the importer is ready. We have to save them one at a time because
   * otherwise we introduce a race condition on the backend, thus the
   * recursive async function.
   */
  const customFields = importer.getUsedCustomFields(CustomFields);

  async function saveCustomFields(fields: CustomField[]): Promise<void> {
    const field = fields.pop();
    if (!field) {
      return;
    }
    await saveModel(field);
    await saveCustomFields(fields);
  }

  await saveCustomFields(customFields);
  CustomFields.refreshApiCache();
  await importer.patchDirty();
}

async function onSave(
  importer: Importer,
  customFieldsCollection: CustomFieldsCollection,
  url: string,
  redirect: (defaultRedirect?: string) => void,
  t: TFunction,
  accountId: string,
): Promise<void> {
  try {
    importer.set({ state: 'pending' });

    await handleSave(importer, customFieldsCollection);
    logUserEvents({
      [`Imported ${importer.isOptOut ? 'opt out' : 'contact'} list`]: {
        account_id: accountId,
        importer_id: importer.id,
      },
    });
    posthog.capture('contact_list_imported', {
      account_id: accountId,
      importer_id: importer.id,
      is_opt_out: importer.isOptOut,
    });
    redirect(url);

    toast(t('Started import.'), { type: 'success' });

    // If err is instance of Error, it should be of type any.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    const message = e.json ? e.json.message : e.toString();
    toast(t("Couldn't start import. {{message}}", { message }), { type: 'error' });
  }
}

function DataImportPage(): JSX.Element {
  const { CustomFields, Importers } = useStore();
  const { importerId } = useParams<{ importerId: string }>();
  const [currentStep, setCurrentStep] = useState(0);
  // Furthest step reached this session; WizardV2 only navigates back to reached steps.
  const [reachedStep, setReachedStep] = useState(0);
  const hasLoggedCreatedRef = useRef(false);
  const { hash, search } = useLocation();
  // Read once: hash sync rewrites the hash to #step1 as soon as it is enabled.
  const [initialHash] = useState(hash);
  const [isStepResolved, setIsStepResolved] = useState(false);
  const isRevampEnabled = isImportsRevampEnabled(useFlags());
  const user = useUser();
  const { t } = useTranslation();

  const importer = Importers.get(importerId!);

  const importState = importer.get('state');
  const completedStep = importState === 'complete' ? LAST_STEP : reachedStep;

  // A step link can only open a step the import has already reached, which a draft has not.
  useEffect(() => {
    if (!isRevampEnabled || importer.isPending || isStepResolved) {
      return;
    }
    const hashStep = parseStepFromHash(initialHash);
    if (hashStep !== undefined) {
      setCurrentStep(Math.min(hashStep, completedStep));
    }
    setIsStepResolved(true);
  }, [completedStep, importer.isPending, initialHash, isStepResolved, isRevampEnabled]);

  useHashSync({
    enabled: isRevampEnabled && isStepResolved,
    prefix: 'step',
    current: currentStep,
    completed: completedStep,
    linear: true,
    onChange: setCurrentStep,
  });

  // Unlike the vertical Wizard, WizardV2 leaves the page scroll alone between steps. Jump rather
  // than animate: the new step's content is already in place.
  useEffect(() => {
    if (isRevampEnabled) {
      document.getElementById('legacyScrollElement')?.scrollTo({ top: 0 });
    }
  }, [currentStep]);

  const importSteps = [
    <Step key={0} stepIndex={0} title={t('Upload')} />,
    <Step key={1} stepIndex={1} title={t('Map')} />,
    <Step key={2} stepIndex={2} title={t('Review')} />,
  ];

  const steps = (
    <Steps
      completed={importState === 'complete' ? 2 : 0}
      current={currentStep}
      onChange={setCurrentStep}
    >
      {importSteps}
    </Steps>
  );

  function onNext(): void {
    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
    setReachedStep(Math.max(reachedStep, nextStep));
  }

  /*
   * The type is chosen in step 1, so the list page no longer knows it when it creates the draft.
   * Campaign imports never logged this event, and the import itself doesn't record that it came
   * from a campaign, so the campaign buttons' URL marker is what leaves them out.
   */
  function onStepOneNext(): void {
    const shouldLogCreated =
      isRevampEnabled &&
      importState === 'draft' &&
      !isContactsOnlyImport(search) &&
      !hasLoggedCreatedRef.current;
    if (shouldLogCreated) {
      hasLoggedCreatedRef.current = true;
      logUserEvents({
        [`Created ${importer.isOptOut ? 'opt out' : 'contact list'} import`]: {
          account_id: user.get('account'),
          importer_id: importer.id,
        },
      });
    }
    onNext();
  }

  function onPrev(): void {
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
  }

  if (importer.isPending) {
    return <Spinner />;
  }

  async function saveChanges(): Promise<void> {
    // Re-editing a failed import turns it back into a draft, so the status
    // reported on the imports list matches what the record now is.
    if (importer.get('state') === 'failed') {
      importer.set({ state: 'draft' });
    }
    await handleSave(importer, CustomFields);
    toast(t('Saved draft.'), { type: 'success' });
  }

  const pageTitle = importer.isOptOut
    ? t('Import Opt Out List')
    : importer.get('name')
      ? importer.get('name')
      : t('Import Data');

  const actions = [
    <Observer key={'draft'}>
      {(): JSX.Element => {
        const isEditable = isImporterEditable(importState);
        return (
          <Button disabled={!isEditable} key={'save'} onClick={saveChanges}>
            {t('Save as draft')}
          </Button>
        );
      }}
    </Observer>,
  ];

  const readOnly = importState === 'complete';

  if (isRevampEnabled) {
    const nextButtonType = readOnly ? 'secondary' : 'primary';

    return (
      <Page title={pageTitle}>
        <ActionBar
          left={
            <>
              <Button disabled={currentStep <= 0} onClick={onPrev}>
                {t('Previous')}
              </Button>
              {currentStep === 0 && (
                <StepOneNextButton
                  importer={importer}
                  onNext={onStepOneNext}
                  type={nextButtonType}
                />
              )}
              {currentStep === 1 && (
                <StepTwoNextButton importer={importer} onNext={onNext} type={nextButtonType} />
              )}
              {currentStep === LAST_STEP && (
                <Button disabled={true} name={'next_step'}>
                  {t('Next')}
                </Button>
              )}
            </>
          }
          right={
            <>
              {actions}
              {currentStep === LAST_STEP && (
                <ImportButton
                  disabled={readOnly || !isImporterEditable(importState)}
                  importer={importer}
                />
              )}
            </>
          }
          usePortal={true}
        />
        <WizardV2
          completed={completedStep}
          current={currentStep}
          isFullWidth={true}
          name={'data-import'}
          onChange={setCurrentStep}
        >
          <WizardV2.Step title={t('Upload')}>
            <div className={styles.stepContent}>
              <DataImportStepOne
                disabled={readOnly}
                importer={importer}
                onNext={onStepOneNext}
                showActions={false}
              />
            </div>
          </WizardV2.Step>
          <WizardV2.Step title={t('Map')}>
            <div className={styles.stepContent}>
              <DataImportStepTwo
                disabled={readOnly}
                importer={importer}
                onNext={onNext}
                onPrev={onPrev}
                showActions={false}
              />
            </div>
          </WizardV2.Step>
          <WizardV2.Step title={t('Review')}>
            <div className={styles.stepContent}>
              <DataImportStepThree
                disabled={readOnly}
                ImportButton={ImportButton}
                importer={importer}
                onPrev={onPrev}
                showActions={false}
              />
            </div>
          </WizardV2.Step>
        </WizardV2>
      </Page>
    );
  }

  return (
    <Page title={pageTitle}>
      <Wizard actions={actions} className={styles.wizard} steps={steps}>
        {currentStep === 0 && importer && (
          <DataImportStepOne disabled={readOnly} importer={importer} onNext={onStepOneNext} />
        )}
        {currentStep === 1 && (
          <DataImportStepTwo
            disabled={readOnly}
            importer={importer}
            onNext={onNext}
            onPrev={onPrev}
          />
        )}
        {currentStep === 2 && (
          <DataImportStepThree
            disabled={readOnly}
            ImportButton={ImportButton}
            importer={importer}
            onPrev={onPrev}
          />
        )}
      </Wizard>
    </Page>
  );
}

export default observer(DataImportPage);
